import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { FaArrowLeft, FaRegHeart, FaHeart, FaItunesNote, FaPlay, FaEye, FaTimes, FaTrash, FaThumbtack, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { FaShareNodes } from "react-icons/fa6";
import { LuMessageCircleMore } from "react-icons/lu";
import { GiSaveArrow } from "react-icons/gi";
import Loader from '../components/Loader';
import ShareReelModal from '../components/ShareReelModal';

// APIs Import
import { getReelById, incrementViews } from '../api/reelsApi';
import { toggleLike, toggleBookmark, getContentInteractions } from '../api/interactionApi';
import { addComment, getComments, deleteComment, pinComment } from '../api/commentApi';

// Helper to decode JWT token to get current user ID
const getLoggedInUserId = () => {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload).id;
  } catch (e) {
    return null;
  }
};

const SingleReelPage = () => {
  const { id } = useParams(); // URL se reel ki ID nikalna
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [reel, setReel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem("reelsMuted") === "true";
    } catch (e) {
      return false;
    }
  });
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const toggleSound = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (videoRef.current) {
      const nextMuted = !isMuted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
      try {
        localStorage.setItem("reelsMuted", nextMuted ? "true" : "false");
      } catch (err) {}
    }
  };

  // Interactive States
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [saved, setSaved] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);

  const rawSrc = reel?.videoUrl || reel?.url || reel?.video || reel?.streamUrl || "";

  const bunnyEmbedUrl = (() => {
    let vId = reel?.videoId;
    if (!vId && rawSrc && (rawSrc.includes('b-cdn.net') || rawSrc.includes('mediadelivery.net'))) {
      const parts = rawSrc.split('/');
      const found = parts.find(p => p.length >= 32 && (p.includes('-') || p.length === 36));
      if (found && !found.includes('.')) vId = found;
    }
    if (vId) {
      return `https://iframe.mediadelivery.net/embed/711587/${vId}?autoplay=true&loop=true&muted=false`;
    }
    return null;
  })();

  // 1. Fetch Single Reel Data
  useEffect(() => {
    const fetchReel = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getReelById(id);
        const data = response?.reel || response?.data || response;
        setReel(data);

        // Jab reel load ho jaye, tab background me view count badha dein
        incrementViews(id).catch(err => console.log("View increment failed", err));

      } catch (err) {
        console.error("Error fetching single reel:", err);
        setError("This video is unavailable or has been deleted.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReel();
  }, [id]);

  // HLS stream attach effect for standard video elements
  useEffect(() => {
    if (bunnyEmbedUrl) return; // Managed via iframe embed

    const videoElement = videoRef.current;
    if (!videoElement || !rawSrc) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHls = rawSrc.toLowerCase().includes(".m3u8");
    const canNativeHLS = videoElement.canPlayType('application/x-mpegURL') || videoElement.canPlayType('application/vnd.apple.mpegurl');

    const safePlay = () => {
      if (!videoElement) return;
      videoElement.muted = isMutedRef.current;
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            if (err.name === "NotAllowedError" && !videoElement.muted) {
              videoElement.muted = true;
              setIsMuted(true);
              videoElement.play()
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
            } else if (err.name === "AbortError") {
              // Harmless interruption by load request
            } else {
              console.log("Autoplay prevented:", err?.name, err?.message);
              setIsPlaying(false);
            }
          });
      }
    };

    if (isHls && Hls.isSupported() && !canNativeHLS) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(rawSrc);
      hls.attachMedia(videoElement);
      hlsRef.current = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        safePlay();
      });
    } else {
      if (!videoElement.src || (!videoElement.src.endsWith(rawSrc) && videoElement.src !== rawSrc)) {
        videoElement.src = rawSrc;
      }
      if (videoElement.readyState >= 2) {
        safePlay();
      } else {
        const onCanPlay = () => {
          safePlay();
        };
        videoElement.addEventListener("canplay", onCanPlay, { once: true });
        safePlay();
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [rawSrc, bunnyEmbedUrl]);

  // Sync state once reel is loaded
  useEffect(() => {
    if (reel) {
      setLikesCount(Number(reel.likesCount ?? reel.likes?.length ?? 0));
      setCommentsCount(Number(reel.commentsCount ?? reel.comments?.length ?? 0));
      setLiked(Boolean(reel.isLiked || reel.userInteraction === "like"));

      const targetId = reel._id || id;
      if (!targetId) return;

      const fetchInteractions = async () => {
        try {
          const stats = await getContentInteractions(targetId);
          if (stats) {
            if (stats.userInteraction) {
              setLiked(stats.userInteraction === "like");
            }
            if (typeof stats.isBookmarked === "boolean") {
              setSaved(stats.isBookmarked);
            }
            if (typeof stats.likes === "number") {
              setLikesCount(stats.likes);
            }
          }
        } catch (e) {
          console.log("Error fetching stats:", e);
        }
      };
      fetchInteractions();
    }
  }, [reel, id]);

  // Fetch comments when comments section is opened
  useEffect(() => {
    const fetchComments = async () => {
      setIsLoadingComments(true);
      try {
        const res = await getComments(id);
        setComments(res.comments || res.data || []);
        setCommentsCount(res.total || res.comments?.length || 0);
      } catch (e) {
        console.log("Error loading comments:", e);
      } finally {
        setIsLoadingComments(false);
      }
    };

    if (showComments) {
      fetchComments();
    }
  }, [showComments, id]);

  const handleCommentPin = async (commentId, isCurrentlyPinned, e) => {
    e.stopPropagation();
    try {
      const res = await pinComment(commentId, !isCurrentlyPinned);
      if (res && res.success) {
        setComments((prev) => {
          const updated = prev.map((item) => {
            if (item._id === commentId) {
              return { ...item, isPinned: !isCurrentlyPinned };
            }
            return { ...item, isPinned: false };
          });
          return updated.sort((a, b) => {
            if (a.isPinned !== b.isPinned) return b.isPinned ? 1 : -1;
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
        });
      }
    } catch (err) {
      console.error("Pin comment failed", err);
      alert(err.response?.data?.message || "Failed to pin comment");
    }
  };

  // Video Play/Pause Toggle
  const togglePlay = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isPlaying) {
      videoElement.pause();
      setIsPlaying(false);
    } else {
      videoElement.muted = isMutedRef.current;
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.log("Play failed:", err?.name, err?.message);
            if (err.name === "NotAllowedError" && !videoElement.muted) {
              videoElement.muted = true;
              setIsMuted(true);
              videoElement.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            } else {
              setIsPlaying(false);
            }
          });
      }
    }
  };

  // Like Click Handler
  const handleLikeToggle = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) {
      alert("Please log in to like this reel.");
      return;
    }
    const targetId = reel?._id || id;
    if (!targetId) return;

    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikesCount(prev => nextLiked ? prev + 1 : Math.max(0, prev - 1));
    try {
      const res = await toggleLike(targetId);
      if (res && typeof res.totalLikes === "number") {
        setLikesCount(res.totalLikes);
      }
      if (res && typeof res.isLiked === "boolean") {
        setLiked(res.isLiked);
      } else if (res && res.message) {
        const isNowLiked = res.message.toLowerCase().includes("added") || (res.message.toLowerCase().includes("like") && !res.message.toLowerCase().includes("removed"));
        setLiked(isNowLiked);
      }
    } catch (err) {
      console.error("Like toggle failed", err);
      // Rollback
      setLiked(!nextLiked);
      setLikesCount(prev => !nextLiked ? prev + 1 : Math.max(0, prev - 1));
    }
  };

  // Save Click Handler
  const handleSaveToggle = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) {
      alert("Please log in to save this reel.");
      return;
    }
    const targetId = reel?._id || id;
    if (!targetId) return;

    const nextSaved = !saved;
    setSaved(nextSaved);
    try {
      await toggleBookmark(targetId);
    } catch (err) {
      console.error("Bookmark toggle failed", err);
      // Rollback
      setSaved(!nextSaved);
    }
  };

  // Share functionality
  const handleShare = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowShareModal(true);
  };

  // Comment Submit Handler
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    if (!token) {
      alert("Please log in to comment.");
      return;
    }
    try {
      const res = await addComment(id, { text: commentText });
      if (res.success && res.comment) {
        setComments(prev => [res.comment, ...prev]);
        setCommentsCount(prev => prev + 1);
        setCommentText("");
      }
    } catch (err) {
      console.error("Failed to add comment:", err);
      alert("Failed to post comment. Please try again.");
    }
  };

  // Comment Delete Handler
  const handleCommentDelete = async (commentId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await deleteComment(commentId);
      setComments(prev => prev.filter(c => c._id !== commentId));
      setCommentsCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to delete comment:", err);
      alert("Failed to delete comment.");
    }
  };

  if (isLoading) return <Loader />;

  if (error || !reel) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Video Not Found</h2>
        <p className="text-gray-500 mb-6">{error}</p>
        <button onClick={() => navigate(-1)} className="bg-brand-orange text-white px-6 py-2 rounded-xl font-bold">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full sm:w-auto max-w-full bg-black sm:rounded-2xl overflow-hidden relative shadow-2xl h-[calc(100dvh-8rem-env(safe-area-inset-bottom,0px))] sm:aspect-[9/16] sm:h-[calc(100dvh-120px)] sm:max-h-[820px] md:h-[calc(100vh-120px)] md:max-h-[780px] sm:my-auto flex flex-col">

      {/* Top Navigation Bar overlay */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent z-20 text-white">
        <button onClick={() => navigate(-1)} className="text-xl hover:text-brand-orange transition p-2 bg-black/30 rounded-full backdrop-blur-sm z-20">
          <FaArrowLeft />
        </button>
        <div className="flex items-center gap-1.5 text-xs font-bold bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">
          <FaEye className="text-brand-orange" /> {reel.viewsCount || reel.views || 0}
        </div>
      </div>

      {/* Video Player */}
      <div className="flex-1 relative bg-zinc-950 flex justify-center items-center">
        {bunnyEmbedUrl ? (
          <iframe
            src={bunnyEmbedUrl}
            title="Single Reel Video"
            className="w-full h-full border-0 object-cover"
            allow="autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
          ></iframe>
        ) : (
          <video
            ref={videoRef}
            src={rawSrc.toLowerCase().includes(".m3u8") && Hls.isSupported() ? undefined : (rawSrc || undefined)}
            className="w-full h-full object-cover cursor-pointer"
            loop
            playsInline
            muted={isMuted}
            preload="metadata"
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        )}

        {/* Mute/Unmute Audio Button */}
        {!bunnyEmbedUrl && (
          <button
            onClick={toggleSound}
            className="absolute top-16 right-4 z-30 bg-black/60 backdrop-blur-md text-white p-2.5 rounded-full hover:bg-black/80 transition shadow-lg"
            title={isMuted ? "Unmute Audio" : "Mute Audio"}
          >
            {isMuted ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
          </button>
        )}

        {/* Big Play Icon when paused */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="w-20 h-20 bg-black/50 rounded-full flex items-center justify-center text-white text-3xl backdrop-blur-md pl-2 shadow-xl border border-white/10">
              <FaPlay />
            </div>
          </div>
        )}

        {/* Floating Side Actions */}
        <div className="absolute bottom-16 sm:bottom-20 md:bottom-24 right-2 sm:right-3 md:right-4 flex flex-col gap-2.5 sm:gap-3.5 md:gap-5 items-center z-20 text-white animate-fade-in">
          {/* Like button */}
          <button onClick={handleLikeToggle} className="flex flex-col items-center group">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-base sm:text-lg md:text-xl hover:bg-white/20 transition ${liked ? 'text-red-500 scale-110' : ''}`}>
              {liked ? <FaHeart /> : <FaRegHeart />}
            </div>
            <span className="text-[10px] sm:text-xs font-bold mt-0.5 sm:mt-1 drop-shadow-md">{likesCount}</span>
          </button>

          {/* Comment button */}
          <button onClick={() => setShowComments(true)} className="flex flex-col items-center group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-base sm:text-lg md:text-xl group-hover:bg-white/20 transition">
              <LuMessageCircleMore />
            </div>
            <span className="text-[10px] sm:text-xs font-bold mt-0.5 sm:mt-1 drop-shadow-md">{commentsCount}</span>
          </button>

          {/* Save button */}
          <button onClick={handleSaveToggle} className="flex flex-col items-center group">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-base sm:text-lg md:text-xl hover:bg-white/20 transition ${saved ? 'text-orange-500 scale-110' : ''}`}>
              <GiSaveArrow />
            </div>
            <span className={`text-[9px] sm:text-[10px] uppercase font-bold tracking-wide mt-0.5 drop-shadow-md ${saved ? 'text-orange-400 font-extrabold' : ''}`}>
              {saved ? 'Saved' : 'Save'}
            </span>
          </button>

          {/* Share button */}
          <button onClick={handleShare} className="flex flex-col items-center group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-base sm:text-lg md:text-xl group-hover:bg-white/20 transition">
              <FaShareNodes />
            </div>
            <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wide mt-0.5 drop-shadow-md">Share</span>
          </button>
        </div>

        {/* Bottom Description Overlay */}
        <div className="absolute bottom-2 sm:bottom-3 md:bottom-5 left-2 sm:left-3 md:left-4 right-14 sm:right-16 md:right-20 z-20 text-white bg-gradient-to-t from-black/85 via-black/45 to-transparent p-2.5 sm:p-3 md:p-4 rounded-xl backdrop-blur-sm border border-white/10 max-h-[45%] overflow-hidden">
          <h2 className="font-extrabold tracking-wide text-xs sm:text-sm md:text-base mb-1 drop-shadow-md line-clamp-1">
            {reel.title || "My Studio Reel"}
          </h2>
          <p className="text-[11px] sm:text-xs text-gray-200 font-medium mb-1.5 sm:mb-2 line-clamp-2">
            {reel.description || reel.caption || "Enjoy this reel exclusively on CatchWatch."}
          </p>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-300 font-bold bg-white/10 w-fit px-2.5 py-1 rounded-full border border-white/5">
            <FaItunesNote className="text-brand-orange text-xs" />
            <span className="truncate max-w-[150px]">Original Audio - {reel.user?.name || "User"}</span>
          </div>
        </div>
      </div>

      {/* Comments Drawer overlay */}
      {showComments && (
        <div className="absolute inset-0 bg-black/60 z-30 flex flex-col justify-end" onClick={() => setShowComments(false)}>
          <div
            className="w-full bg-zinc-900 border-t border-white/10 rounded-t-2xl max-h-[60%] flex flex-col p-4 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center pb-3 border-b border-white/10 mb-3">
              <span className="font-extrabold text-sm sm:text-base">Comments ({commentsCount})</span>
              <button
                onClick={() => setShowComments(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition"
              >
                <FaTimes />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 animate-fade-in">
              {isLoadingComments ? (
                <div className="text-center py-6 text-zinc-400 text-xs font-semibold">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="text-center py-10 text-zinc-500 text-xs font-bold">No comments yet. Start the conversation!</div>
              ) : (
                comments.map((c) => {
                  const isOwnComment = c.user?._id === getLoggedInUserId();
                  const isReelCreator = Boolean(
                    reel?.user?._id === getLoggedInUserId() ||
                    reel?.user === getLoggedInUserId()
                  );

                  const isVerifiedCreator = isReelCreator && (
                    Boolean(reel?.user?.orangeTick || reel?.user?.blueTick || reel?.user?.isVerified || reel?.user?.verification?.isVerified || reel?.user?.verification?.status === "VERIFIED" || reel?.user?.verification?.status === "APPROVED") ||
                    (() => {
                      try {
                        const u = JSON.parse(localStorage.getItem("user") || localStorage.getItem("userInfo") || "{}");
                        return Boolean(u?.orangeTick || u?.blueTick || u?.isVerified || u?.verification?.isVerified || u?.verification?.status === "VERIFIED" || u?.verification?.status === "APPROVED");
                      } catch (e) { return false; }
                    })()
                  );

                  return (
                    <div
                      key={c._id}
                      className={`flex gap-2.5 items-start justify-between p-2.5 rounded-xl border transition ${
                        c.isPinned
                          ? "bg-amber-950/40 border-amber-500/50 shadow-sm"
                          : "bg-white/5 border-white/5"
                      }`}
                    >
                      <div className="flex gap-2.5 items-start flex-1 min-w-0">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-brand-orange flex-shrink-0 flex items-center justify-center font-bold text-xs border border-white/20 overflow-hidden">
                          {c.user?.profileImage ? (
                            <img src={c.user.profileImage || null} alt="user" className="w-full h-full object-cover" />
                          ) : (
                            (c.user?.name || "U")[0].toUpperCase()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Pinned Tag */}
                          {c.isPinned && (
                            <div className="flex items-center gap-1 text-[10px] font-black text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full w-fit mb-1">
                              <FaThumbtack className="text-amber-400 text-[9px]" />
                              <span>Pinned by Creator</span>
                            </div>
                          )}
                          <div className="text-xs font-black text-orange-400 truncate">
                            {c.user?.name || c.user?.username || "Anonymous"}
                          </div>
                          <div className="text-[12px] text-zinc-200 mt-0.5 break-words font-medium leading-relaxed">
                            {c.text}
                          </div>
                          <div className="text-[9px] text-zinc-500 mt-1 font-semibold">
                            {new Date(c.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Pin Button ONLY for Verified Orange Tick Reel Creator */}
                        {isVerifiedCreator && (
                          <button
                            onClick={(e) => handleCommentPin(c._id, c.isPinned, e)}
                            className={`p-1.5 rounded transition text-[11px] flex items-center justify-center ${
                              c.isPinned
                                ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                                : "bg-white/5 text-zinc-400 hover:text-amber-400 hover:bg-white/10"
                            }`}
                            title={c.isPinned ? "Unpin Comment" : "Pin Comment to Top"}
                          >
                            <FaThumbtack className={c.isPinned ? "text-amber-400" : "rotate-45"} />
                          </button>
                        )}

                        {isOwnComment && (
                          <button
                            onClick={(e) => handleCommentDelete(c._id, e)}
                            className="text-zinc-500 hover:text-red-500 transition text-[11px] p-1.5 bg-white/5 hover:bg-red-500/10 rounded"
                            title="Delete Comment"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input Form */}
            <form onSubmit={handleCommentSubmit} className="mt-3 flex gap-2 pt-3 border-t border-white/10">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none focus:border-brand-orange"
              />
              <button
                type="submit"
                className="bg-brand-orange hover:bg-orange-600 text-white font-bold text-xs px-4 py-2 rounded-xl transition"
              >
                Post
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Share Reel Modal */}
      {reel && (
        <ShareReelModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          reel={reel}
        />
      )}

    </div>
  );
};

export default SingleReelPage;